import { Link } from 'react-router-dom'
import { Card, CardHeader } from '../ui/Card'
import type { StaffModuleCode } from '../../config/staffModules'

export type DashboardQuickAction = {
  id: string
  label: string
  description: string
  href: string
  emphasis?: boolean
  badge?: string
}

type BuildQuickActionsInput = {
  canCatalog: boolean
  canSales: boolean
  canPayments: boolean
  canStorefront: boolean
  canPos: boolean
  pendingFulfillmentCount: number
}

export function buildDashboardQuickActions(input: BuildQuickActionsInput): DashboardQuickAction[] {
  const actions: DashboardQuickAction[] = []

  if (input.canSales && input.pendingFulfillmentCount > 0) {
    actions.push({
      id: 'fulfill-orders',
      label: 'Fulfill pending orders',
      description: `${input.pendingFulfillmentCount} order${input.pendingFulfillmentCount === 1 ? '' : 's'} awaiting shipment or pickup`,
      href: '/orders',
      emphasis: true,
      badge: String(input.pendingFulfillmentCount),
    })
  }

  if (input.canCatalog) {
    actions.push({
      id: 'add-product',
      label: 'Add product',
      description: 'Publish a new item to your storefront catalog',
      href: '/products/new',
      emphasis: !input.canSales || input.pendingFulfillmentCount === 0,
    })
    actions.push({
      id: 'manage-products',
      label: 'Manage products',
      description: 'Edit pricing, inventory, and visibility',
      href: '/products',
    })
  }

  if (input.canSales) {
    actions.push({
      id: 'view-orders',
      label: 'View orders',
      description: 'Track status, payments, and fulfillment',
      href: '/orders',
    })
    actions.push({
      id: 'transactions',
      label: 'Transactions',
      description: 'Online payments, POS sales, and wallet activity',
      href: '/transactions',
    })
  }

  if (input.canPayments) {
    actions.push({
      id: 'payments',
      label: 'Payments & wallet',
      description: 'Gateways, balance, and checkout options',
      href: '/commerce',
    })
    actions.push({
      id: 'withdrawals',
      label: 'Withdrawals',
      description: 'Request a payout and track status',
      href: '/withdrawals',
    })
  }

  if (input.canStorefront) {
    actions.push({
      id: 'storefront',
      label: 'Store profile',
      description: 'Hero, trust badges, and homepage content',
      href: '/storefront',
    })
  }

  if (input.canPos) {
    actions.push({
      id: 'pos',
      label: 'Make a sale',
      description: 'Record an in-store POS transaction',
      href: '/pos/sales',
    })
  }

  return actions
}

type DashboardQuickActionsProps = {
  canCatalog: boolean
  canSales: boolean
  canPayments: boolean
  canStorefront: boolean
  canPos: boolean
  pendingFulfillmentCount?: number
}

function QuickActionTile({ action }: { action: DashboardQuickAction }) {
  return (
    <Link
      to={action.href}
      className={`group flex min-h-[5.5rem] flex-col justify-between rounded-xl border p-4 transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        action.emphasis
          ? 'border-primary/40 bg-primary/5 hover:border-primary/60'
          : 'border-border bg-card/50 hover:border-border hover:bg-muted/20'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-foreground group-hover:text-primary">{action.label}</p>
        {action.badge ? (
          <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground tabular-nums">
            {action.badge}
          </span>
        ) : null}
      </div>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{action.description}</p>
    </Link>
  )
}

export function DashboardQuickActions({
  canCatalog,
  canSales,
  canPayments,
  canStorefront,
  canPos,
  pendingFulfillmentCount = 0,
}: DashboardQuickActionsProps) {
  const actions = buildDashboardQuickActions({
    canCatalog,
    canSales,
    canPayments,
    canStorefront,
    canPos,
    pendingFulfillmentCount,
  })

  if (actions.length === 0) return null

  return (
    <Card>
      <CardHeader title="Quick actions" description="Jump to the tasks that matter most right now." />
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {actions.map((action) => (
          <li key={action.id}>
            <QuickActionTile action={action} />
          </li>
        ))}
      </ul>
    </Card>
  )
}

export function staffModuleFlags(
  role: string | undefined,
  modules: string[] | undefined | null,
  posEnabled: boolean,
): Record<StaffModuleCode, boolean> {
  const isOrgAdmin = role === 'org_admin'
  const has = (code: StaffModuleCode) => isOrgAdmin || (modules ?? []).includes(code)
  return {
    dashboard: has('dashboard'),
    catalog: has('catalog'),
    sales: has('sales'),
    payments: has('payments'),
    storefront: has('storefront'),
    settings: has('settings'),
    pos: has('pos') && posEnabled,
  }
}
